import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Pencil,
  X,
  Plus,
  Loader2,
  Check,
  Users,
  Inbox,
  Settings,
  Copy,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { savedEmailsApi, adminApi, clientsApi } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const cleanText = (v) =>
  String(v ?? "")
    .replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, " ")
    .trim();

const backupLabels = {
  email_depot: "Sauvegarde Dépôts",
  email_restitution: "Sauvegarde Restitutions",
  email_delivrance: "Sauvegarde Délivrances",
};

export default function AdminContacts() {
  const [saved, setSaved] = useState([]);
  const [backup, setBackup] = useState({});
  const [clientsWithEmail, setClientsWithEmail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  // Inline-edit / create state for SavedEmails
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  // Refs to read DOM values at submit time (safer than state alone).
  const labelRef = useRef(null);
  const emailRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [savedList, backupData, clientsList] = await Promise.all([
        savedEmailsApi.list(),
        adminApi.backupEmails(),
        clientsApi.list({ sort_by: "nom" }),
      ]);
      setSaved(savedList);
      setBackup(backupData || {});
      setClientsWithEmail(
        (clientsList || []).filter((c) => c.email && c.email.includes("@"))
      );
    } catch (err) {
      toast.error("Impossible de charger le carnet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setAdding(false);
    setEditingId(null);
    setLabel("");
    setEmail("");
  };

  const handleStartEdit = (it) => {
    setAdding(true);
    setEditingId(it.id);
    setLabel(it.label);
    setEmail(it.email);
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const lbl = cleanText(labelRef.current?.value ?? label);
    const eml = cleanText(emailRef.current?.value ?? email);
    if (!lbl) {
      toast.error("Le libellé est obligatoire");
      labelRef.current?.focus();
      return;
    }
    if (!eml || !EMAIL_RE.test(eml)) {
      toast.error("Email invalide", {
        description: 'Format attendu : "nom@domaine.com"',
      });
      emailRef.current?.focus();
      return;
    }
    setLabel(lbl);
    setEmail(eml);
    setBusy(true);
    try {
      if (editingId) {
        await savedEmailsApi.update(editingId, { label: lbl, email: eml });
        toast.success("Email mis à jour");
      } else {
        await savedEmailsApi.create({ label: lbl, email: eml });
        toast.success("Email mémorisé");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Supprimer "${it.label} — ${it.email}" ?`)) return;
    try {
      await savedEmailsApi.remove(it.id);
      toast.success("Email supprimé");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const handleCopy = async (em) => {
    try {
      await navigator.clipboard.writeText(em);
      toast.success(`${em} copié`);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  // Search filter applied to all 3 sections
  const q = query.trim().toLowerCase();
  const matchEm = (it, ...fields) =>
    !q || fields.filter(Boolean).some((f) => String(f).toLowerCase().includes(q));

  const visibleSaved = saved.filter((s) => matchEm(s, s.label, s.email));
  const visibleBackup = ["email_depot", "email_restitution", "email_delivrance"]
    .filter((k) => backup[k])
    .filter((k) => matchEm(null, backupLabels[k], backup[k]));
  const visibleClients = clientsWithEmail.filter((c) =>
    matchEm(c, c.nom, c.prenom, c.email, c.numero_client, c.ville)
  );

  const totalCount =
    visibleSaved.length + visibleBackup.length + visibleClients.length;

  if (loading) {
    return <div className="p-8 text-[var(--vidoc-text-secondary)]">Chargement…</div>;
  }

  return (
    <div data-testid="admin-contacts-page">
      <PageHeader
        title="Carnet d'adresses"
        subtitle="Tous vos contacts email centralisés"
      />

      <div className="px-4 md:px-8 py-6 max-w-4xl space-y-6">
        {/* Search bar */}
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vidoc-text-tertiary)]" />
          <input
            data-testid="contacts-search-bar"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher (libellé, email, nom client…)"
            className="w-full pl-10 pr-3 py-3 rounded-xl bg-white border border-[var(--vidoc-border)] focus:border-[var(--vidoc-primary)] outline-none touch-target"
          />
        </div>
        {q && (
          <div className="text-xs text-[var(--vidoc-text-secondary)] px-1">
            {totalCount} résultat(s) pour "{q}"
          </div>
        )}

        {/* Section 1: Saved extra emails (ADV, CP…) */}
        <section data-testid="section-saved-emails">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Settings className="w-4 h-4 text-[var(--vidoc-primary)]" />
              Emails mémorisés (ADV, CP, comptable…)
              <span className="text-xs text-[var(--vidoc-text-tertiary)] font-normal">
                · {saved.length}
              </span>
            </h2>
            {!adding && (
              <button
                data-testid="contacts-new-saved-btn"
                onClick={() => setAdding(true)}
                className="text-xs font-medium text-[var(--vidoc-primary)] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[var(--vidoc-bg-secondary)]"
              >
                <Plus className="w-3.5 h-3.5" />
                Nouveau
              </button>
            )}
          </div>

          <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
            {adding && (
              <form
                onSubmit={handleSave}
                noValidate
                className="p-3 bg-[var(--vidoc-bg-secondary)] space-y-2"
              >
                <div className="text-xs font-semibold text-[var(--vidoc-text-secondary)]">
                  {editingId ? "Modifier" : "Nouvel email à mémoriser"}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[120px,1fr] gap-3">
                  <div>
                    <label
                      htmlFor="contacts-label-field"
                      className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--vidoc-text-secondary)] mb-1"
                    >
                      Libellé
                    </label>
                    <input
                      id="contacts-label-field"
                      ref={labelRef}
                      data-testid="contacts-label-input"
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="ADV, CP…"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      required
                      className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--vidoc-border)] focus:border-[var(--vidoc-primary)] outline-none text-sm"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contacts-email-field"
                      className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--vidoc-text-secondary)] mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="contacts-email-field"
                      ref={emailRef}
                      data-testid="contacts-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="exemple@maison.com"
                      autoComplete="email"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      inputMode="email"
                      required
                      className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--vidoc-border)] focus:border-[var(--vidoc-primary)] outline-none text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    data-testid="contacts-save-btn"
                    disabled={busy}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[var(--vidoc-primary)] text-white text-sm font-medium disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    {editingId ? "Mettre à jour" : "Mémoriser"}
                  </button>
                  <button
                    type="button"
                    data-testid="contacts-cancel-btn"
                    onClick={resetForm}
                    disabled={busy}
                    className="px-3 py-1.5 rounded-lg bg-white border border-[var(--vidoc-border)] text-sm font-medium hover:bg-[var(--vidoc-bg-secondary)] disabled:opacity-50"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            )}

            {visibleSaved.length === 0 && !adding ? (
              <div className="px-4 py-6 text-center text-sm text-[var(--vidoc-text-secondary)]">
                {q ? "Aucun email mémorisé ne correspond." : "Aucun email mémorisé pour l'instant."}
              </div>
            ) : (
              visibleSaved.map((it) => (
                <Row
                  key={it.id}
                  testid={`saved-row-${it.id}`}
                  badge={<Badge color="primary">{it.label}</Badge>}
                  email={it.email}
                  onCopy={() => handleCopy(it.email)}
                  actions={
                    <>
                      <ActionBtn
                        testid={`saved-edit-${it.id}`}
                        title="Modifier"
                        onClick={() => handleStartEdit(it)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </ActionBtn>
                      <ActionBtn
                        testid={`saved-delete-${it.id}`}
                        title="Supprimer"
                        onClick={() => handleDelete(it)}
                        danger
                      >
                        <X className="w-3.5 h-3.5" />
                      </ActionBtn>
                    </>
                  }
                />
              ))
            )}
          </div>
        </section>

        {/* Section 2: Backup emails (read-only here, link to admin page) */}
        <section data-testid="section-backup-emails">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Inbox className="w-4 h-4 text-[var(--vidoc-primary)]" />
              Emails de sauvegarde
              <span className="text-xs text-[var(--vidoc-text-tertiary)] font-normal">
                · {Object.values(backup).filter((v) => v && typeof v === "string").length}
              </span>
            </h2>
            <Link
              to="/admin/backup-emails"
              data-testid="contacts-go-backup"
              className="text-xs font-medium text-[var(--vidoc-primary)] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[var(--vidoc-bg-secondary)]"
            >
              Modifier
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
            {visibleBackup.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-[var(--vidoc-text-secondary)]">
                {q ? "Aucun email de sauvegarde ne correspond." : "Aucun email de sauvegarde configuré."}
              </div>
            ) : (
              visibleBackup.map((k) => (
                <Row
                  key={k}
                  testid={`backup-row-${k}`}
                  badge={<Badge color="amber">{backupLabels[k]}</Badge>}
                  email={backup[k]}
                  onCopy={() => handleCopy(backup[k])}
                />
              ))
            )}
          </div>
        </section>

        {/* Section 3: Client emails */}
        <section data-testid="section-client-emails">
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[var(--vidoc-primary)]" />
            Emails clients
            <span className="text-xs text-[var(--vidoc-text-tertiary)] font-normal">
              · {clientsWithEmail.length}
            </span>
          </h2>

          <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
            {visibleClients.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-[var(--vidoc-text-secondary)]">
                {q ? "Aucun client ne correspond." : "Aucun client n'a d'email enregistré."}
              </div>
            ) : (
              visibleClients.map((c) => (
                <Row
                  key={c.id}
                  testid={`client-email-row-${c.id}`}
                  badge={
                    <Badge color="neutral">
                      {c.nom}
                      {c.prenom ? ` ${c.prenom}` : ""}
                    </Badge>
                  }
                  hint={[c.numero_client && `N° ${c.numero_client}`, c.ville].filter(Boolean).join(" · ")}
                  email={c.email}
                  onCopy={() => handleCopy(c.email)}
                  actions={
                    <Link
                      to={`/clients/${c.id}`}
                      data-testid={`client-go-${c.id}`}
                      className="p-1.5 rounded-lg hover:bg-[var(--vidoc-bg-secondary)] text-[var(--vidoc-text-secondary)]"
                      title="Ouvrir la fiche"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  }
                />
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Row({ testid, badge, email, hint, onCopy, actions }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--vidoc-bg-secondary)]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center flex-wrap gap-2">
          {badge}
          <button
            type="button"
            onClick={onCopy}
            className="text-sm font-medium text-[var(--vidoc-text)] hover:text-[var(--vidoc-primary)] truncate"
            title="Cliquer pour copier"
          >
            {email}
          </button>
        </div>
        {hint && (
          <div className="text-xs text-[var(--vidoc-text-tertiary)] truncate mt-0.5">
            {hint}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          data-testid={`${testid}-copy`}
          onClick={onCopy}
          className="p-1.5 rounded-lg hover:bg-white text-[var(--vidoc-text-secondary)]"
          title="Copier l'email"
          aria-label="Copier"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        {actions}
      </div>
    </div>
  );
}

function Badge({ color = "neutral", children }) {
  const cls = {
    primary: "bg-blue-50 text-[var(--vidoc-primary)] border-blue-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    neutral: "bg-[var(--vidoc-bg-secondary)] text-[var(--vidoc-text)] border-[var(--vidoc-border)]",
  }[color];
  return (
    <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}>
      {children}
    </span>
  );
}

function ActionBtn({ children, testid, title, onClick, danger }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`p-1.5 rounded-lg ${
        danger
          ? "text-[var(--vidoc-danger)] hover:bg-red-50"
          : "text-[var(--vidoc-text-secondary)] hover:bg-white"
      }`}
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  );
}
